import React from 'react';

import {
  InsertDriveFile as InsertDriveFileIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  listItemButtonClasses,
  listItemIconClasses,
  Stack,
  Typography,
} from '@mui/material';

import { formatFeinNumber } from '@hrlogics/helper-utils';

import { Nullable } from '@clearverify/types';

import { GeneratedReportMetaInfoFragment } from 'src/api';
import { Popover } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { SxProp } from 'src/types/mui';

import { GeneratedReportPreviewCardAction } from './types';

export type GeneratedReportPreviewCardProps = {
  generatedReportMeta: GeneratedReportMetaInfoFragment;
  onPreview: (report: GeneratedReportMetaInfoFragment) => Promise<void> | void;
  actions?: GeneratedReportPreviewCardAction[];
};

export const GeneratedReportPreviewCard: React.FC<GeneratedReportPreviewCardProps> = ({
  generatedReportMeta,
  onPreview,
  actions,
}) => {
  const [loading, setLoading] = React.useState(false);

  const handlePreviewClick = React.useCallback(async () => {
    setLoading(true);

    try {
      await onPreview(generatedReportMeta);
    } finally {
      setLoading(false);
    }
  }, [generatedReportMeta, onPreview]);

  const renderPopoverContent = React.useCallback(
    ({ onClose }: { onClose: () => void }): React.ReactNode => {
      const handleAction =
        (
          onAction: (
            generatedReportMeta: Nullable<GeneratedReportMetaInfoFragment>,
          ) => Promise<void> | void,
        ) =>
        async () => {
          await onAction(generatedReportMeta);
          onClose();
        };

      return (
        <Box sx={popoverContentStyles}>
          <List sx={popoverListStyles}>
            {actions?.map(({ onAction, name, icon, color }) => (
              <ListItem key={name} disablePadding sx={{ color }}>
                <ListItemButton onClick={handleAction(onAction)}>
                  <ListItemIcon>{icon}</ListItemIcon>
                  <ListItemText primary={name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      );
    },
    [actions, generatedReportMeta],
  );

  return (
    <Card sx={cardContainerStyles} variant="outlined">
      {actions && (
        <CardHeader
          action={
            <Popover
              target={
                <IconButton>
                  <MoreVertIcon />
                </IconButton>
              }
              content={renderPopoverContent}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              elevation={4}
            />
          }
          sx={{
            position: 'absolute',
            right: 0,
          }}
        />
      )}

      <CardContent>
        <Stack sx={{ pt: 2 }} alignItems="center">
          <Box fontSize="40px">
            <InsertDriveFileIcon color="action" fontSize="inherit" />
          </Box>
          <Typography noWrap sx={typographyStyles}>
            {generatedReportMeta.file.filename}
          </Typography>
          <Typography noWrap sx={typographyStyles} color="text.secondary">
            <DemoPrivacyText>
              {generatedReportMeta.employerDisplayName || 'Unknown Employer'}
            </DemoPrivacyText>
          </Typography>
          <Typography noWrap fontSize="0.75rem" color="text.secondary" mt={0}>
            <DemoPrivacyText>
              {formatFeinNumber(generatedReportMeta.feinValue) || '—'}
            </DemoPrivacyText>
          </Typography>
          <Button onClick={handlePreviewClick} disabled={loading} loading={loading}>
            Preview
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

const cardContainerStyles: SxProp = {
  position: 'relative',
  width: '100%',
  maxHeight: '205px',
  borderRadius: 2,
};

const typographyStyles: SxProp = {
  textOverflow: 'ellipsis',
  width: '100%',
  fontVariant: 'body1',
  textAlign: 'center',
};

const popoverContentStyles: SxProp = {
  width: 180,
};

const popoverListStyles: SxProp = {
  mt: -1,
  paddingBottom: 0,

  [`.${listItemButtonClasses.root}`]: {
    paddingX: 2.5,
  },
  [`.${listItemIconClasses.root}`]: {
    minWidth: 42,
    color: 'inherit',
  },
};
